"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
	useEffect,
	useLayoutEffect,
	useRef,
	type KeyboardEvent,
	type ReactNode,
	type RefObject,
} from "react";

interface ToolDialogProps {
	open: boolean;
	onClose: () => void;
	title: string;
	className: string;
	children: ReactNode;
	initialFocusRef: RefObject<HTMLElement | null>;
	onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
}

/** Motion is scoped to calculator sheets; Radix owns focus, dismissal and scroll lock. */
export function ToolDialog({
	open,
	onClose,
	title,
	className,
	children,
	initialFocusRef,
	onKeyDown,
}: ToolDialogProps) {
	const previousFocus = useRef<HTMLElement | null>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (open) return;
		const rememberFocus = (event: FocusEvent) => {
			if (event.target instanceof HTMLElement && !contentRef.current?.contains(event.target)) {
				previousFocus.current = event.target;
			}
		};
		document.addEventListener("focusin", rememberFocus);
		return () => document.removeEventListener("focusin", rememberFocus);
	}, [open]);
	useLayoutEffect(() => {
		if (open) initialFocusRef.current?.focus();
		else if (previousFocus.current?.isConnected) previousFocus.current.focus();
	}, [open, initialFocusRef]);
	return (
		<DialogPrimitive.Root
			open={open}
			onOpenChange={(next) => {
				if (!next) onClose();
			}}
		>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="tool-dialog-overlay fixed inset-0 z-50 bg-black/50" />
				<DialogPrimitive.Content
					ref={contentRef}
					aria-describedby={undefined}
					aria-hidden={!open}
					inert={!open}
					onOpenAutoFocus={(event) => {
						const active = document.activeElement;
						if (
							active instanceof HTMLElement &&
							active !== document.body &&
							!contentRef.current?.contains(active)
						) {
							previousFocus.current = active;
						}
						event.preventDefault();
						initialFocusRef.current?.focus();
					}}
					onCloseAutoFocus={(event) => {
						event.preventDefault();
						previousFocus.current?.focus();
					}}
					onKeyDown={onKeyDown}
					className={`tool-dialog-content fixed z-50 flex max-h-[85vh] w-full flex-col rounded-t-2xl border border-border bg-background shadow-xl sm:max-h-[80vh] sm:rounded-2xl ${className}`}
				>
					<DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
					{children}
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
